import {
  ArrowButtonDownBold,
  ArrowButtonUpBold,
  CloseBold,
  InformationCircleBold,
} from '@ha/icons';
import React from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import {
  FixedFooter,
  Grid,
  Typography,
  IconButton,
  Button,
  Dialog,
  units,
} from '@hbf/dsl';
import { useIntl } from 'ha/i18n';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { PaymentOverviewButton } from 'ha/pages/Listing/components/PaymentOverviewButton';
import {
  DATEPICKER_VARIANT,
  DateRangePicker,
  DateRangePickerInputsNarrow,
} from '@ha/marketplace/feature-date-picker';
import { constants } from 'ha/constants/commonConstants';
import { DATE_RANGE_FORMAT } from 'ha/constants/Dates';

import StudapartIcon from 'ha/custom-icons/studapart.svg';

import { ListingContactAdvertiserButton } from '../../fragments/ListingContactAdvertiserButton';
import { ListingLinkToPartner } from '../ListingLinkToPartner';
import { ListingPrice } from '../../fragments/ListingPrice';
import { useDesiredDatesPicker } from '../../containers/DesiredDatesPicker';

import { getIsBelongsToPartner } from '../../selectors';

import { useDesiredDates } from '../../hooks/useDesiredDates';
import {
  ListingAdvancedPricingInfo,
  ListingAdvancedPricingPill,
} from '../../fragments/ListingAdvancedPricing';
import { useStudapart } from '../../hooks/useStudapart';
import { StudapartInfo } from '../StudapartInfo';

const useStyles = makeStyles()(theme => ({
  container: {
    padding: theme.spacing(2, 0),
  },
  spacer: {
    padding: theme.spacing(0.5),
  },
  arrowIcon: {
    position: 'absolute',
    right: theme.spacing(2.25),
    top: theme.spacing(3),
    color: theme.palette.grey[700],
  },
  expandable: {
    overflow: 'hidden',
    transition: 'max-height 0.4s ease',
    height: 'auto',
    maxHeight: '50vh',
    paddingBottom: theme.spacing(0.5),
  },
  expandableClosed: {
    maxHeight: 0,
  },
  priceContainerWithArrow: {
    paddingRight: theme.spacing(4),
  },
  studapartContainer: {
    textAlign: 'right',
    marginBottom: -theme.spacing(0.5),
  },
  studapartIcon: {
    height: units.rem(0.875),
    width: 'auto',
    marginRight: theme.spacing(0.5),
  },
  studapartDialogIcon: {
    height: units.rem(1.25),
    width: 'auto',
  },
  infoIcon: {
    fontSize: units.rem(0.875),
    color: theme.palette.grey[600],
  },
  infoButton: {
    marginRight: -theme.spacing(1),
  },
  dialogContainer: {
    padding: theme.spacing(2),
    paddingTop: theme.spacing(1),
  },
}));

const ListingOverviewMobile: React.FC = () => {
  const { classes, cx } = useStyles();
  const { T, formatDate, locale } = useIntl();
  const { sm } = useMediaQuery();

  const [expand, setExpand] = React.useState(true);

  const { hasDesiredDates, desiredDates } = useDesiredDates();

  React.useEffect(() => {
    if (hasDesiredDates) {
      setExpand(true);
    }
  }, [hasDesiredDates]);
  const isBelongsToPartner = useSelector(getIsBelongsToPartner);
  const { currentValue, onDateChange, dateRangePickerProps } =
    useDesiredDatesPicker();

  const { isStudapartListing } = useStudapart();
  const [studapartInfoOpen, setStudapartInfoOpen] =
    React.useState<boolean>(false);
  const handleOpenClick = React.useCallback(() => {
    setStudapartInfoOpen(true);
  }, []);
  const handleClose = React.useCallback(() => {
    setStudapartInfoOpen(false);
  }, []);

  return (
    <FixedFooter appBarProps={{ elevation: 0 }} dividerTop>
      <div
        className={classes.container}
        data-test-locator="ListingOverviewMobile"
      >
        {hasDesiredDates && (
          <div
            className={classes.arrowIcon}
            onClick={() => {
              setExpand(!expand);
            }}
          >
            {expand ? (
              <ArrowButtonDownBold fontSize="small" />
            ) : (
              <ArrowButtonUpBold fontSize="small" />
            )}
          </div>
        )}
        <Grid
          container
          justifyContent="space-between"
          alignItems="flex-end"
          wrap="nowrap"
          className={
            hasDesiredDates ? classes.priceContainerWithArrow : undefined
          }
        >
          <Grid item>
            <ListingPrice variant="mobile" />
          </Grid>
          {isStudapartListing && (
            <Grid item className={classes.studapartContainer}>
              <Typography.Caption color="muted">
                {T('ldp.studapart.caption_mobile')}
              </Typography.Caption>
              <Button onClick={handleOpenClick} className={classes.infoButton}>
                <StudapartIcon className={classes.studapartIcon} />
                <InformationCircleBold className={classes.infoIcon} />
              </Button>
              <Dialog fullScreen open={studapartInfoOpen} onClose={handleClose}>
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                  className={classes.dialogContainer}
                >
                  <StudapartIcon className={classes.studapartDialogIcon} />
                  <IconButton
                    onClick={handleClose}
                    className={classes.infoButton}
                    size="large"
                  >
                    <CloseBold fontSize="small" className={classes.infoIcon} />
                  </IconButton>
                  <Grid item xs={12}>
                    <StudapartInfo />
                  </Grid>
                </Grid>
              </Dialog>
            </Grid>
          )}
        </Grid>

        <div className={classes.spacer} />
        {hasDesiredDates && (
          <div
            className={cx(classes.expandable, {
              [classes.expandableClosed]: !expand,
            })}
          >
            <PaymentOverviewButton
              text={T('StructuredCosts.LDP.cta_fullpaymentoverview')}
              startDate={formatDate(DATE_RANGE_FORMAT, desiredDates.startDate!)}
            />

            <div className={classes.spacer} />

            <DateRangePicker
              header={<ListingAdvancedPricingInfo />}
              CaptionSubtitle={ListingAdvancedPricingPill}
              variant={DATEPICKER_VARIANT.RESPONSIVE}
              DatePickerInputs={DateRangePickerInputsNarrow}
              onDateChange={onDateChange}
              value={currentValue}
              showSingleMonth={!sm}
              T={T}
              formatDate={formatDate}
              localeString={locale}
              listingFlexibleDays={constants.listingFlexibleDays}
              {...dateRangePickerProps}
            />
          </div>
        )}

        <div className={classes.spacer} />

        <div data-test-locator="Contact Advertiser Wrapper">
          {isBelongsToPartner ? (
            <ListingLinkToPartner
              label={
                isStudapartListing ? T('View on %s', 'Studapart') : undefined
              }
            />
          ) : (
            <ListingContactAdvertiserButton />
          )}
        </div>
      </div>
    </FixedFooter>
  );
};

export { ListingOverviewMobile };
